import {
  LanguageNameSpaces,
  TradeExtraInfoGeneralMapData,
} from "@shared/types";
import Title from "common/Typography/Title.tsx";
import Row from "antd/es/row";
import Col from "antd/es/col";
import ExtraInfoItem from "common/ExtraDetails/ExtraInfoItem.tsx";
import React, { CSSProperties, useContext, useEffect, useMemo } from "react";
import { sharedLib } from "@shared/services/shared.service.ts";
import useQuery from "utils/hooks/useQuery.ts";
import { useRouteMatch } from "react-router-dom";
import {
  COIN_EXTRA_DETAILS_PATH,
  COIN_EXTRA_DETAILS_TABS,
} from "common/constants.ts";
import {
  MARGIN_TYPE_TO_DISPLAY,
  TIME_RANGE_1_DAY,
} from "@shared/common/constants.ts";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay.tsx";
import TradingViewChart from "common/Charts/TradingViewChart.tsx";
import { Divider } from "common/styles.ts";
import styled, { useTheme } from "styled-components";
import { useClientsData } from "@shared/utils/hooks/misc.ts";
import Text from "common/Typography/Text.tsx";
import { skipToken } from "@reduxjs/toolkit/query";
import { ExtraDetailsDrawerContext } from "common/ExtraDetails/ExtraDetailsDrawer.context.ts";
import usePositionItem from "features/portfolio/common/usePositionItem.tsx";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { TranslationKeys } from "@shared/common/i18n/types.ts";
import NewLookDetailsGrid from "features/portfolio/NewLook/NewLookDetailsGrid.tsx";
import useNewLookPositionCells from "features/portfolio/NewLook/useNewLookPositionCells.tsx";
import { Panel } from "common/NewLook";

const PositionGeneralTab = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);
  const { setTabQueries } = useContext(ExtraDetailsDrawerContext) || {};
  const match = useRouteMatch(COIN_EXTRA_DETAILS_PATH);
  const query = useQuery();
  const accountId = query.get("account_id");
  const reverse = query.get("reverse");
  const marginType = query.get("margin_type");
  const { getAccountById } = useClientsData();
  const theme = useTheme();
  const cells = useNewLookPositionCells();

  const {
    getMargin,
    getPositionSize,
    getUnrealizedPnl,
    getEntryPrice,
    getLiquidationPrice,
    getQuantity,
    getROI,
  } = usePositionItem({});

  const matchParams = (match?.params || {}) as {
    coin: string;
    tab: string;
  };
  const symbol = matchParams.coin ? decodeURIComponent(matchParams.coin) : "";

  const args = useMemo(() => {
    if (!symbol || !accountId || !marginType || !reverse) return skipToken;
    return {
      symbol,
      account_id: Number(accountId),
      reverse: reverse === "true",
      margin_type: Number(marginType),
    };
  }, [symbol, accountId, reverse, marginType]);

  const getPositionExtraGeneralDataQuery =
    sharedLib.sharedApi.portfolio.useGetPositionExtraGeneralDataQuery(args);
  const { currentData: positionData } = getPositionExtraGeneralDataQuery;

  useEffect(() => {
    setTabQueries?.(COIN_EXTRA_DETAILS_TABS.GENERAL, [
      getPositionExtraGeneralDataQuery,
    ]);
  }, [getPositionExtraGeneralDataQuery]);

  const account = getAccountById(positionData?.account_id);
  const isShort = !!positionData?.quantity && positionData.quantity < 0;

  const TitleToFieldValue = {
    [GeneralTitles.ACCOUNT]: account?.client_name,
    [GeneralTitles.EXCHANGE]: positionData?.exchange,
    [GeneralTitles.LONG_SHORT]: (
      <Text type={isShort ? "danger" : "success"}>
        {isShort ? t("positions.short") : t("positions.long")}
      </Text>
    ),
    [GeneralTitles.LEVERAGE]: positionData?.leverage
      ? `${positionData.leverage}x`
      : undefined,
    [GeneralTitles.MARGIN_TYPE]: positionData?.margin_type
      ? MARGIN_TYPE_TO_DISPLAY[positionData?.margin_type]
      : undefined,
    [GeneralTitles.POSITION_SIZE]: (
      <>
        {getPositionSize(positionData)}
        {getQuantity(positionData)}
      </>
    ),
    [GeneralTitles.MARGIN]: getMargin(positionData),
    [GeneralTitles.UNREALIZED_PNL]: (
      <>
        {getUnrealizedPnl(positionData)} {getROI(positionData)}
      </>
    ),
    [GeneralTitles.ENTRY_PRICE]: getEntryPrice(positionData),
    [GeneralTitles.LIQUIDATION_PRICE]: getLiquidationPrice(positionData),
  };

  const getDisplayedData = (section: GeneralTabSections) => {
    return (
      <div key={section}>
        <StyledTitle>{t(section)}</StyledTitle>
        <Row gutter={[16, 16]}>
          {GENERAL_TAB_SECTION_TO_TITLES[section].map((title) => {
            return (
              <Col span={12} key={title}>
                <ExtraInfoItem
                  name={t(title)}
                  value={TitleToFieldValue[title]}
                  style={EXTRA_INFO_STYLE}
                  ellipsis={true}
                />
              </Col>
            );
          })}
        </Row>
      </div>
    );
  };

  const generalData = useMemo(() => {
    return Object.values(GeneralTabSections).map((section, index) => {
      const isLast = index === Object.values(GeneralTabSections).length - 1;
      return (
        <div key={index}>
          {getDisplayedData(section)}
          {!isLast && <StyledDivider />}
        </div>
      );
    });
  }, [account, positionData]);

  const newLookFieldValue: Partial<Record<GeneralTitles, React.ReactNode>> =
    positionData
      ? {
          [GeneralTitles.LONG_SHORT]: (
            <span
              style={{ color: isShort ? theme.colors.red : theme.colors.green }}
            >
              {isShort ? t("positions.short") : t("positions.long")}
            </span>
          ),
          [GeneralTitles.POSITION_SIZE]: cells.size(positionData, false),
          [GeneralTitles.MARGIN]: cells.margin(positionData, false),
          [GeneralTitles.UNREALIZED_PNL]: cells.pnl(positionData, false),
          [GeneralTitles.LIQUIDATION_PRICE]: cells.liquidation(positionData),
        }
      : {};

  const newLookGeneralData = theme.isNewLook && (
    <NewLookSections>
      <Panel title={t("charts.price_change")} flush={true}>
        <NewLookChart>
          <TradingViewChart
            symbol={symbol}
            exchange={positionData?.exchange}
            defaultTimeRange={TIME_RANGE_1_DAY}
            spinnerSize="default"
            minimize={true}
          />
        </NewLookChart>
      </Panel>
      {Object.values(GeneralTabSections).map((section) => (
        <NewLookDetailsGrid
          key={section}
          title={t(section)}
          items={GENERAL_TAB_SECTION_TO_TITLES[section].map((title) => ({
            key: title,
            label: t(title),
            value: newLookFieldValue[title] ?? TitleToFieldValue[title],
          }))}
        />
      ))}
    </NewLookSections>
  );

  return (
    <LoadingSpinnerOverlay
      isLoading={!positionData}
      spinnerSize="large"
      hideContentWhileLoading={true}
    >
      {newLookGeneralData || (
        <>
          <ChartWrapper>
            <TradingViewChart
              symbol={symbol}
              exchange={positionData?.exchange}
              defaultTimeRange={TIME_RANGE_1_DAY}
              spinnerSize="default"
              minimize={true}
            />
          </ChartWrapper>
          {generalData}
        </>
      )}
    </LoadingSpinnerOverlay>
  );
};

export default PositionGeneralTab;

enum GeneralTabSections {
  ACCOUNT_STATUS = "position_sections.account_status",
  POSITION_DETAILS = "position_sections.position_details",
}

const EXTRA_INFO_STYLE: CSSProperties = {
  flexDirection: "column",
  alignItems: "flex-start",
  height: "100%",
  justifyContent: "flex-start",
};

enum GeneralTitles {
  ACCOUNT = "positions.account",
  EXCHANGE = "positions.exchange",
  LONG_SHORT = "positions.long_short",
  LEVERAGE = "positions.leverage",
  MARGIN_TYPE = "positions.margin_type",
  POSITION_SIZE = "positions.position_size",
  MARGIN = "positions.margin",
  UNREALIZED_PNL = "positions.unrealized_pnl",
  ENTRY_PRICE = "positions.entry_price",
  LIQUIDATION_PRICE = "positions.liquidation_price",
}

const GENERAL_TAB_SECTION_TO_TITLES: Record<
  GeneralTabSections,
  TranslationKeys<LanguageNameSpaces.PORTFOLIO>[]
> = {
  [GeneralTabSections.ACCOUNT_STATUS]: [
    GeneralTitles.ACCOUNT,
    GeneralTitles.EXCHANGE,
  ],
  [GeneralTabSections.POSITION_DETAILS]: [
    GeneralTitles.LONG_SHORT,
    GeneralTitles.LEVERAGE,
    GeneralTitles.MARGIN_TYPE,
    GeneralTitles.POSITION_SIZE,
    GeneralTitles.MARGIN,
    GeneralTitles.UNREALIZED_PNL,
    GeneralTitles.ENTRY_PRICE,
    GeneralTitles.LIQUIDATION_PRICE,
  ],
};

const NewLookChart = styled.div`
  height: 360px;
  /* Same inset as the panel's title, so the chart lines up with it. */
  padding: 8px 24px 16px;
`;
const NewLookSections = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;
const StyledDivider = styled(Divider)`
  border-color: ${(p) => p.theme.colors.lightestBorderGrey};
`;
const ChartWrapper = styled.div`
  height: 400px;
  margin-bottom: 28px;
`;
const StyledTitle = styled(Title).attrs({ level: 2 })`
  font-weight: ${(p) => p.theme.typography.fontWeights.SEMI_BOLD};
  margin-bottom: 16px;
`;
